import {
  type ActionFunction,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from "@remix-run/node";
import {
  ClientActionFunction,
  ClientLoaderFunctionArgs,
  json,
  Outlet,
  useFetcher,
  useLoaderData,
  useLocation,
  useParams,
} from "@remix-run/react";
import React, { useContext, useEffect, useRef } from "react";
import ToastContext from "~/contexts/ToastContext";
import {
  buildDroppedItemsToast,
  changeSiteSource,
  DroppedItem,
} from "~/helpers/changeSite";
import { shouldClearOrder } from "~/helpers/orderClear";
import {
  clearSessionOrder,
  commitPersistentSession,
  commitSession,
  getAuthSession,
  getPersistentSession,
  getSession,
  setSessionOrder,
} from "~/sessions";
import invariant from "tiny-invariant";

import Api from "~/api/api";
import PageWrapper from "~/components/PageWrapper/PageWrapper";
import ThemeContext from "~/contexts/ThemeContext";
import { resolveMenuCode } from "~/helpers/menuHelpers";
import BaseLayout from "~/layouts/BaseLayout/BaseLayout";
import { getServiceStatus } from "~/helpers/siteHelpers";
import { urlOrderSource } from "~/routes/$orderSource";
import { OrderContextProvider } from "~/contexts/OrderContext";
import { MenuContextProvider } from "~/contexts/MenuContext";
import { BannerContextProvider } from "~/contexts/BannerContext";
import { OnlineBanner } from "~/entities/bannerEntities";
import { FlashPromotion } from "~/entities/flashPromotionEntities";
import { FlashPromotionContextProvider } from "~/contexts/FlashPromotionContext";
import { TaxContextProvider } from "~/contexts/TaxContext";
import { SiteContextProvider } from "~/contexts/SiteContext";
import { SerializeFrom } from "@remix-run/server-runtime/dist/serialize";
import { PaymentContextProvider } from "~/contexts/PaymentContext";
import {
  cartToReq,
  OrderStatus,
  ResponseOrderWithCartLines,
  ResponseOrderWithLines,
} from "~/entities/orderEntities";
import {
  getJsonFormValue,
  getOptionalBoolFormValue,
  getOptionalJsonFormValue,
  getOptionalJSONNullStrFormValue,
  getOptionalStrFormValue,
  getStrFormValue,
} from "~/helpers/formHelpers";
import DefaultErrorBoundary from "~/components/DefaultErrorBoundary";
import AuthContext from "~/contexts/AuthContext";

import { makeAuthHeaders } from "~/api/endpoints/Auth";
import {
  getBestMatchingSlot,
  getOrderTypeCode,
  orderTypeCodeValid,
} from "~/helpers/orderHelper";
import { v4 } from "uuid";
import { iconMeta } from "~/helpers/remixHelpers";
import { ErrorType, isErrorType } from "~/modules/checkout/constants";
import { ShouldRevalidateFunctionArgs } from "@remix-run/router/utils";
import { isPaymentPage } from "~/helpers/routesHelpers";
import SignBackInModal from "~/modules/auth/containers/SignBackInModal/SignBackInModal";
import { isBefore } from "date-fns";
import StoreHeader from "~/modules/order/components/StoreHeader/StoreHeader";
import { ApiError } from "~/api/ApiError";
import type { DeliveryReasonCode } from "~/helpers/deliveryCheck";

// Delivery error types thrown by addOrder/updateOrder when the store cannot
// deliver to the cart's delivery address. We surface these as a calm modal
// rather than letting them hit the route ErrorBoundary.
const DELIVERY_UNAVAILABLE_ERROR_TYPES: ReadonlySet<DeliveryReasonCode> =
  new Set<DeliveryReasonCode>([
    "NO_DELIVERY_AVAILABLE",
    "ROUTE_NOT_FOUND",
    "INVALID_DELIVERY_ADDRESS",
  ]);

const isDeliveryUnavailableError = (
  e: unknown,
): e is ApiError & { errorType: DeliveryReasonCode } =>
  e instanceof ApiError &&
  DELIVERY_UNAVAILABLE_ERROR_TYPES.has(e.errorType as DeliveryReasonCode);

// The order was already placed (e.g. a stray cart-sync/reprice fired right after
// payment transitioned it to SENT). The update is a no-op — swallow it instead
// of crashing to the ErrorBoundary; the order is done.
const isOrderAlreadyPlacedError = (e: unknown): e is ApiError =>
  e instanceof ApiError && e.errorType === "INVALID_ORDER_STATUS";

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const pageTitle = data?.siteInfo?.domainSettings?.title
    ? data?.siteInfo?.domainSettings?.title
    : data?.siteInfo?.site?.siteName;
  return [
    iconMeta(matches),
    {
      title: pageTitle
        ? `${pageTitle} - Product selection`
        : "Product selection",
    },
    {
      name: "description",
      content: data?.siteInfo?.domainSettings?.description
        ? `${data?.siteInfo?.domainSettings?.description} - Product selection page`
        : "Product selection page",
    },
  ];
};

export const action: ActionFunction = async ({ request, params }) => {
  const session = await getSession(request.headers.get("Cookie"));
  const authSession = await getAuthSession(request.headers.get("Cookie"));
  const persistentSession = await getPersistentSession(
    request.headers.get("Cookie"),
  );

  let sessionModified = false;
  let persistentSessionModified = false;

  const makeResponseHeaders = async (): Promise<[string, string][]> => {
    const headers: [string, string][] = [];
    if (sessionModified) {
      headers.push(["Set-Cookie", await commitSession(session)]);
    }
    if (persistentSessionModified) {
      headers.push([
        "Set-Cookie",
        await commitPersistentSession(persistentSession),
      ]);
    }
    return headers;
  };

  const authenticatedUserId = authSession.get("authenticatedUserId");
  const accessToken = authSession.get("accessToken");
  const authHeaders = makeAuthHeaders(request, accessToken);

  const formData = await request.formData();

  let orderId = session.get("orderId");
  const orgId = getStrFormValue(formData, "orgId");

  const saveCustomerDetails = getOptionalBoolFormValue(
    formData,
    "allowSavePersonalData",
  );

  invariant(params.orderSource, "params.orderSource expected");
  invariant(params.siteId, "params.siteId expected");

  // Set new requested time in session if provided
  const setRequestedTime = getOptionalJSONNullStrFormValue(
    formData,
    "setRequestedTime",
  );
  if (setRequestedTime !== undefined) {
    session.set("requestedReadyTime", setRequestedTime);
    sessionModified = true;
  }

  // Set new order type in session if provided
  const setOrderTypeCode = getOptionalStrFormValue(
    formData,
    "setOrderTypeCode",
  );
  if (setOrderTypeCode) {
    session.set("orderTypeCode", setOrderTypeCode);
    sessionModified = true;
  }

  // Set new delivery address in session if provided
  const setDeliveryInfo = getOptionalJsonFormValue(formData, "setDeliveryInfo");
  if (setDeliveryInfo !== undefined) {
    session.set("deliveryInfo", setDeliveryInfo);
    sessionModified = true;
  }

  const redirectToPayment = async () => {
    const url = `/${params.orderSource}/${params.siteId}${
      params.table ? "/" + params.table : ""
    }/payment`;

    return redirect(url, { headers: await makeResponseHeaders() });
  };

  if (getOptionalBoolFormValue(formData, "isCancelPayment") && orderId) {
    const paymentIds: string[] = getJsonFormValue(formData, "paymentIds");
    const siteId = params.siteId;
    const oId = orderId; // Needed so typescript doesn't complain about the value of orderId potentially changing
    try {
      await Promise.all(
        paymentIds.map((x) =>
          Api.Payment.cancelPayment(authHeaders, orgId, siteId, oId, x),
        ),
      );
    } catch {
      return await redirectToPayment();
    }
    session.unset("paymentId");
    sessionModified = true;
    return json(
      { success: true },
      {
        headers: await makeResponseHeaders(),
      },
    );
  }

  if (getOptionalBoolFormValue(formData, "isReset")) {
    clearSessionOrder(session);
    sessionModified = true;

    // If there is an error clearing the order, we still need to return success so that the user can start over with ease
    if (orderId) {
      try {
        await Api.Order.clearOrder(authHeaders, orgId, params.siteId, orderId);
      } catch (e) {
        console.warn("error clearing order", e);
      }
    }

    return json(
      { success: true },
      {
        headers: await makeResponseHeaders(),
      },
    );
  }

  if (formData.get("forceInsert")?.toString() && orderId) {
    orderId = undefined;
    // The code later will update with the new orderId on successful insert, so no need to modify session here
  }

  const orderSummaryFromForm = formData.get("orderSummary")?.toString();
  invariant(orderSummaryFromForm, "orderSummary is required");

  const orderSummary: ResponseOrderWithCartLines =
    JSON.parse(orderSummaryFromForm);

  // If response order id is not empty, but session order id is
  // e.g. if session expired so we lost order id reference,
  // or if we have requested a force insert
  // then generate new ids for all lines
  if (
    orderSummary.id != null &&
    orderSummary.id !== "" &&
    orderSummary.id !== orderId
  ) {
    console.log("Generating new ids for every line");
    for (const line of orderSummary.lines) {
      line.id = v4();
    }
  }

  const promises: Promise<any>[] = [];
  if (saveCustomerDetails) {
    if (authenticatedUserId) {
      promises.push(
        Api.Auth.updateCustomer(
          request,
          authHeaders,
          authenticatedUserId,
          orderSummary.customer,
        ),
      );
    } else {
      persistentSession.set("guestUser", orderSummary.customer);
      persistentSessionModified = true;
    }
  }

  // If site has changed, store "prevSite"
  const prevSite = persistentSession.get("prevSite");
  if (!prevSite || prevSite !== params.siteId) {
    persistentSession.set("prevSite", params.siteId);
    persistentSessionModified = true;
  }

  const errorOnInvalidAsset = getOptionalBoolFormValue(
    formData,
    "errorOnInvalidAsset",
  );

  // Prevent insert/update when specified in form
  const preventInsert = getOptionalBoolFormValue(formData, "preventInsert");
  const preventUpdate = getOptionalBoolFormValue(formData, "preventUpdate");

  try {
    const order = cartToReq(orderSummary);
    let resOrder: ResponseOrderWithLines | null = null;

    try {
      if (!orderId) {
        if (!preventInsert) {
          resOrder = await Api.Order.addOrder(
            authHeaders,
            orgId,
            params.siteId,
            order,
            errorOnInvalidAsset,
          );
          setSessionOrder(session, resOrder.id, params.siteId);
          sessionModified = true;
        }
      } else {
        if (!preventUpdate) {
          try {
            resOrder = await Api.Order.updateOrder(
              authHeaders,
              orgId,
              params.siteId,
              orderId,
              order,
              errorOnInvalidAsset,
            );
          } catch (e: any) {
            // Update failed due to the order not being found, we will fall back to adding instead.
            if (e.status === 404 && !preventInsert) {
              resOrder = await Api.Order.addOrder(
                authHeaders,
                orgId,
                params.siteId,
                order,
                errorOnInvalidAsset,
              );
              setSessionOrder(session, resOrder.id, params.siteId);
              sessionModified = true;
            } else {
              throw e;
            }
          }
        }
      }
    } catch (e) {
      // The store cannot deliver to this cart's delivery address. Surface a
      // calm modal (handled in OrderContext) instead of throwing to the route
      // ErrorBoundary. Other errors keep propagating to the outer handler.
      if (isDeliveryUnavailableError(e)) {
        return json(
          { deliveryUnavailable: true, deliveryReason: e.errorType },
          { headers: await makeResponseHeaders() },
        );
      }
      // A spurious update on an already-placed order (SENT) must not crash the
      // success page — treat it as a no-op.
      if (isOrderAlreadyPlacedError(e)) {
        return json(
          { orderAlreadyPlaced: true },
          { headers: await makeResponseHeaders() },
        );
      }
      throw e;
    }

    // A successful add/update means the order now definitively lives at this
    // site. Keep orderSiteId in lockstep — covers the update path (orderId
    // unchanged) and self-heals legacy carts written before orderSiteId existed.
    if (resOrder) {
      setSessionOrder(session, resOrder.id, params.siteId);
      sessionModified = true;
    }

    // If promo code was removed due to being longer valid redirect to show that error
    // Don't do this when currently trying to redeem a promo code / asset (in which case errorOnInvalidAsset will be set)
    if (resOrder && !errorOnInvalidAsset) {
      if (
        (order.redemptionKey && !resOrder.redemptionKey) ||
        (order.promoCode && !resOrder.promoCode)
      ) {
        // Redirect to show the error modal for error messages we have configured to show a user message
        const url = `/${params.orderSource}/${params.siteId}${
          params.table ? "/" + params.table : ""
        }?errorType=${
          order.redemptionKey && !resOrder.redemptionKey
            ? ErrorType.ASSET_AUTO_REMOVED
            : ErrorType.PROMO_CODE_AUTO_REMOVED
        }`;

        return redirect(url, {
          headers: await makeResponseHeaders(),
        });
      }
    }

    await Promise.all(promises);

    if (formData.get("redirectToPayment")) {
      return await redirectToPayment();
    }

    return json(
      { success: true, orderId, resOrder },
      { headers: await makeResponseHeaders() },
    );
  } catch (error: any) {
    // Prefer userMessage, which is the human readable message from the backend
    // (error.message is often the machine readable errorType, e.g. CODE_REDEMPTION_FAILED)
    const message =
      error?.userMessage ||
      error?.message ||
      "An error occurred while processing your order";
    if (
      error.errorType === "CODE_REDEMPTION_FAILED" ||
      error.errorType === "ASSET_REDEMPTION_FAILED"
    ) {
      return json(
        {
          success: false,
          errorType: error.errorType,
          message,
        },
        { status: 200 },
      );
    } else if (error.errorType && isErrorType(error.errorType)) {
      // Redirect to show the error modal for error messages we have configured to show a user message
      const url = `/${params.orderSource}/${params.siteId}${
        params.table ? "/" + params.table : ""
      }?errorType=${error.errorType}`;

      return redirect(url, {
        headers: await makeResponseHeaders(),
      });
    } else {
      throw json(
        { error: message },
        {
          status: 400,
          headers: await makeResponseHeaders(),
        },
      );
    }
  }
};

export const clientAction: ClientActionFunction = async ({
  request,
  serverAction,
}) => {
  const url = new URL(request.url);

  if (url.searchParams.get("clearCache") === "true") {
    clearLoaderCache();
  }
  const res: any = await serverAction();

  if (cachedLoaderData && res.resOrder) {
    cachedLoaderData.orderInfo = res.resOrder;
  }

  return res;
};

export async function loader({ params, request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get("Cookie"));
  const authSession = await getAuthSession(request.headers.get("Cookie"));
  const persistentSession = await getPersistentSession(
    request.headers.get("Cookie"),
  );

  let existingOrderId = session.get("orderId");

  invariant(params.siteId, "expected params.siteId");
  invariant(params.orderSource, "expected params.orderSource");

  const url = new URL(request.url);
  const orderTypeCodeParam = url.searchParams.get("orderTypeCode");
  let menuCode = url.searchParams.get("menuCode");

  const siteInfo = await Api.Site.getSite(request, params.siteId);

  const orderSource = urlOrderSource[params.orderSource];

  // Defensive reset on cross-source navigation: if the URL's orderSource
  // differs from the one we last saw in this session (e.g. /online → /catering
  // on the same Ponsonby), wipe the stuck orderTypeCode so the validate-or-
  // reset block below resolves a fresh default for the new source. Without
  // this, a stale ONLINE_COLLECTION can survive into a /catering visit and
  // the customer sees the regular menu instead of the catering one.
  const lastUrlOrderSource = session.get("urlOrderSource");
  if (lastUrlOrderSource !== params.orderSource) {
    session.unset("orderTypeCode");
    session.set("urlOrderSource", params.orderSource);
  }

  let orderTypeCode = session.get("orderTypeCode");

  if (orderTypeCodeParam) {
    session.set("orderTypeCode", orderTypeCodeParam);
    orderTypeCode = orderTypeCodeParam;
  }

  // Carry a delivery address chosen on /sites into the order's deliveryInfo so it
  // isn't lost on the hand-off (same session key the change-modal writes via setDeliveryInfo).
  const deliveryAddressParam = url.searchParams.get("deliveryAddress");
  const deliveryLatParam = url.searchParams.get("deliveryLat");
  const deliveryLngParam = url.searchParams.get("deliveryLng");
  if (deliveryAddressParam && deliveryLatParam && deliveryLngParam) {
    session.set("deliveryInfo", {
      address: {
        address1: deliveryAddressParam,
        address2: "",
        city: "",
        countryId: null,
        postalCode: "",
        state: "",
        latitude: Number(deliveryLatParam),
        longitude: Number(deliveryLngParam),
      },
    });
  }

  if (
    !orderTypeCode ||
    !orderTypeCodeValid(siteInfo.site, orderSource, orderTypeCode)
  ) {
    orderTypeCode = getOrderTypeCode(orderSource, siteInfo.site.orderTypes);
    session.set("orderTypeCode", orderTypeCode);
  }

  // ── Task 13: Switch-store keeps cart ─────────────────────────────────
  // When the user navigates to a different store while holding an order, clone
  // the order to the new site via changeSite instead of discarding it.
  //
  // The migration SOURCE is the order's OWN tracked home site (orderSiteId) —
  // never prevSite. prevSite means "the last store the user touched" (UI) and
  // drifts out of sync with where the order actually lives, which let a
  // round-trip (A→B→A) skip the migration and strand the cart under the wrong
  // site. We fall back to prevSite only for legacy cookies written before
  // orderSiteId existed (the backend also resolves the order by id, so a stale
  // source no longer 404s — see ChangeSite handler).
  const orderSiteId =
    session.get("orderSiteId") ?? persistentSession.get("prevSite");
  let droppedItems: DroppedItem[] = [];

  const migrateFrom = changeSiteSource({
    orderId: existingOrderId,
    orderSiteId,
    currentSiteId: params.siteId,
  });

  if (migrateFrom && existingOrderId) {
    try {
      const accessToken = authSession.get("accessToken");
      const authHeaders = makeAuthHeaders(request, accessToken);
      const changeSiteResult = await Api.Order.changeSite(
        authHeaders,
        siteInfo.orgId,
        migrateFrom, // source site (the order's real home)
        existingOrderId, // source order
        params.siteId, // destination site
      );
      // Re-home the order: point the session at the new order id AND record
      // that it now lives at this site. The two move together (setSessionOrder)
      // so the next navigation can't misjudge where the order is.
      existingOrderId = changeSiteResult.newOrder.id;
      setSessionOrder(session, changeSiteResult.newOrder.id, params.siteId);
      droppedItems = changeSiteResult.droppedItems;
    } catch (e) {
      // changeSite failed (e.g. auth required but user is guest, or the source
      // order no longer exists). orderSiteId is left untouched, so the order is
      // preserved at its original site and recovered by navigating back; only
      // the new site shows an empty cart.
      console.warn("changeSite failed, falling back to empty cart:", e);
    }
  }
  // ── End Task 13 ───────────────────────────────────────────────────────

  // The engine validates promo codes against the order's REQUESTED ready time,
  // not now, so a flash promotion's liveness has to be asked about that time.
  // A stale session value is dropped rather than sent — the endpoint falls back
  // to now, which is what an ASAP order wants anyway.
  const requestedReadyTimeForFlash = (() => {
    const raw = session.get("requestedReadyTime");
    if (!raw || isBefore(new Date(raw), new Date())) {
      return null;
    }
    return raw;
  })();

  const [
    menus,
    currency,
    taxes,
    orderInfo,
    availableTimeSlots,
    orderAsapWait,
    banners,
    flashPromotion,
  ] = await Promise.all([
    Api.Menu.getMenus(siteInfo.orgId, siteInfo.site.id, orderTypeCode),
    Api.Currency.getCurrency(siteInfo.orgId, siteInfo.site.id),
    Api.Tax.getTaxes(siteInfo.orgId, siteInfo.site.id),
    existingOrderId
      ? Api.Order.tryGetOrder(siteInfo.orgId, siteInfo.site.id, existingOrderId)
      : null,
    Api.Site.getSiteTimeSlots(siteInfo.orgId, siteInfo.site.id, orderTypeCode),
    existingOrderId
      ? Api.Site.getAsapWaitForOrder(
          siteInfo.orgId,
          siteInfo.site.id,
          orderTypeCode,
          existingOrderId,
        ).then((x) => x?.asapWait)
      : null,
    // Banners are marketing content - never fail the page over them
    Api.Banners.getBanners(request, siteInfo.site.id, orderTypeCode).catch(
      (): OnlineBanner[] => [],
    ),
    // Same for the flash promotion
    Api.FlashPromotions.getFlashPromotion(
      request,
      siteInfo.site.id,
      orderTypeCode,
      requestedReadyTimeForFlash,
    ).catch((): FlashPromotion | null => null),
  ]);

  // Self-heal: the order resolved at this site, so record this as its home.
  // Protects legacy carts (cookie predates orderSiteId) on the next store
  // switch, and keeps orderSiteId authoritative after every successful load.
  if (
    orderInfo &&
    existingOrderId &&
    session.get("orderSiteId") !== params.siteId
  ) {
    session.set("orderSiteId", params.siteId);
  }

  // There are various reasons we might want to force the user's cart to be clear (e.g. on payment completion)
  // however we still want to provide the details of the previous state of the order (e.g. for the payment completion screen)
  // Resolve the menu from the order's lines when the URL didn't specify one
  // (a live draft only — a completed order shouldn't dictate the menu).
  // The working cart's menu — only relevant for a live draft.
  const cartMenuCode =
    orderInfo?.status === OrderStatus.DRAFT && orderInfo.lines?.length
      ? orderInfo.lines[0].menuCode
      : null;
  // Is the cart's menu actually one of THIS order type's menus? A cart carried
  // in from another experience (e.g. an ONLINE cart into /catering) is "foreign"
  // and will be cleared below; for time selection we then use the session slot.
  const cartMenuIsForThisOrderType =
    !!cartMenuCode && menus.some((m) => m.code === cartMenuCode);
  // A draft cart whose items belong to a different menu (carried in from
  // another experience) is "foreign" and will be cleared below. An empty draft
  // is NOT foreign. This mirrors the clientLoader's `!clearOrder` rule so the
  // server and client agree on the requested time.
  const cartIsForeign = !!cartMenuCode && !cartMenuIsForThisOrderType;

  let sessionRequestedTime = session.get("requestedReadyTime");
  // Ignore session requested time if it is in the past
  if (
    sessionRequestedTime &&
    isBefore(new Date(sessionRequestedTime), new Date())
  ) {
    sessionRequestedTime = null;
  }

  // Use the requested ready time from the order (a live draft whose cart belongs
  // here, including an empty draft) or the closest slot matching the session
  // time. Equivalent to the prior `!clearOrder && orderInfo` condition.
  const requestedReadyTime =
    orderInfo && orderInfo.status === OrderStatus.DRAFT && !cartIsForeign
      ? orderInfo.requestedReadyTime
      : getBestMatchingSlot(availableTimeSlots.times, sessionRequestedTime);

  // Resolve the menu against THIS order type's menus: URL code, then the cart's
  // menu, then the active menu — skipping any code not in `menus` (the catering
  // 500: an ONLINE menu code fetched under a CATERING order type → mongo 404).
  menuCode = resolveMenuCode(
    menus,
    [menuCode, cartMenuCode],
    requestedReadyTime,
    siteInfo.site.timeZone,
  );

  // Whether to hide the working cart. Deliberately keeps the cart across a
  // Pickup ⇄ Delivery switch (re-priced, Sal's behaviour) and across multiple
  // menus that are all open — only a completed order, or a line on a foreign or
  // time-closed menu, clears it (mirrors the backend's per-menu availability
  // check at placement). See helpers/orderClear.ts.
  const clearOrder = orderInfo
    ? shouldClearOrder({
        order: {
          status: orderInfo.status,
          lineMenuCodes: orderInfo.lines?.map((l) => l.menuCode) ?? [],
        },
        menus,
        requestedReadyTime,
        timeZone: siteInfo.site.timeZone,
      })
    : false;

  // No menu for this experience at all → graceful empty state (the index route
  // renders "No active menu found"), never a 500.
  const loadedMenu = menuCode
    ? await Api.Menu.getMenu(
        siteInfo.orgId,
        siteInfo.site.id,
        menuCode,
        orderTypeCode,
      )
    : null;

  const deliveryInfo = session.get("deliveryInfo") || null;

  return json(
    {
      siteInfo,
      siteId: params.siteId,
      // URL source segment ("online"/"catering"/"table-ordering"). Carried so
      // the clientLoader can detect a cross-source navigation (e.g. /online →
      // /catering on the same site) and force a fresh server load — otherwise
      // the cached menu from the previous source sticks (same siteId).
      urlOrderSource: params.orderSource,
      banners,
      flashPromotion,
      menus,
      menu: loadedMenu,
      currency,
      taxes,
      clearOrder,
      orderInfo,
      sessionRequestedTime,
      requestedReadyTime: requestedReadyTime,
      availableTimeSlots: availableTimeSlots.times,
      asapWait: orderAsapWait ?? availableTimeSlots.asapWait,
      tableNumber: orderInfo?.tableNumber ?? null,
      guestUser: persistentSession.get("guestUser"),
      orderTypeCode,
      asapAvailable: availableTimeSlots.asapWait != null,
      deliveryInfo,
      droppedItems,
    },
    {
      headers: {
        "Set-Cookie": await commitSession(session),
      },
    },
  );
}

let cachedLoaderData: SerializeFrom<typeof loader> | null = null;

export const clearLoaderCache = () => {
  cachedLoaderData = null;
};

export async function clientLoader({
  params,
  request,
  serverLoader,
}: ClientLoaderFunctionArgs) {
  invariant(params.siteId, "expected params.siteId");

  const url = new URL(request.url);
  let menuCode = url.searchParams.get("menuCode");
  const errorType = url.searchParams.get("errorType");
  const orderIdParam = url.searchParams.get("orderId");
  const orderTypeCodeParam = url.searchParams.get("orderTypeCode");

  // Force server loader to run if site / menu has changed or there was a handled api error
  // We force it to load on handled api error so that any out of date info displayed (such as whether a service is online or not) is corrected
  if (
    (orderTypeCodeParam &&
      orderTypeCodeParam !== cachedLoaderData?.orderTypeCode) ||
    !cachedLoaderData ||
    cachedLoaderData.siteId !== params.siteId ||
    // Cross-source navigation on the same site (e.g. /online → /catering for
    // the same Ponsonby): force a fresh server load so the orderTypeCode and
    // menu are re-resolved for the new source, instead of inheriting the
    // previous source's cached menu.
    cachedLoaderData.urlOrderSource !== params.orderSource ||
    (orderIdParam && cachedLoaderData?.orderInfo?.id !== orderIdParam) ||
    (menuCode != null && cachedLoaderData.menu?.code !== menuCode) ||
    errorType
  ) {
    cachedLoaderData = await serverLoader();
    return cachedLoaderData;
  }

  // In client loader we will always return cached menu data and order type info
  // (changing order type requires clearing cache). menu is null when the order
  // type has no menu (graceful empty state).
  menuCode = cachedLoaderData.menu?.code ?? null;

  const [orderInfo, asapWait]: [ResponseOrderWithLines | null, number | null] =
    await Promise.all([
      cachedLoaderData.orderInfo?.id
        ? Api.Order.tryGetOrder(
            cachedLoaderData.siteInfo.orgId,
            cachedLoaderData.siteInfo.site.id,
            cachedLoaderData.orderInfo?.id,
          )
        : null,
      cachedLoaderData.requestedReadyTime != null
        ? cachedLoaderData.asapWait
        : cachedLoaderData.orderInfo?.id
        ? Api.Site.getAsapWaitForOrder(
            cachedLoaderData.siteInfo.orgId,
            cachedLoaderData.siteInfo.site.id,
            cachedLoaderData.orderTypeCode,
            cachedLoaderData.orderInfo.id,
          )
            .then(
              (x) =>
                x ||
                Api.Site.getAsapWait(
                  cachedLoaderData!.siteInfo.orgId,
                  cachedLoaderData!.siteInfo.site.id,
                  cachedLoaderData!.orderTypeCode,
                ),
            )
            .then((x) => x.asapWait)
        : Api.Site.getAsapWait(
            cachedLoaderData.siteInfo.orgId,
            cachedLoaderData.siteInfo.site.id,
            cachedLoaderData.orderTypeCode,
          ).then((x) => x.asapWait),
    ]);

  // There are various reasons we might want to force the user's cart to be clear (e.g. on payment completion)
  // however we still want to provide the details of the previous state of the order (e.g. for the payment completion screen)
  // Same rule as the server loader: keep the cart across a fulfilment switch and
  // across concurrently-open menus; only a completed order, or a line on a
  // foreign or time-closed menu, clears it. See helpers/orderClear.ts.
  const clearOrder = orderInfo
    ? shouldClearOrder({
        order: {
          status: orderInfo.status,
          lineMenuCodes: orderInfo.lines?.map((l) => l.menuCode) ?? [],
        },
        menus: cachedLoaderData.menus,
        requestedReadyTime: cachedLoaderData.requestedReadyTime,
        timeZone: cachedLoaderData.siteInfo.site.timeZone,
      })
    : false;

  // Use the requested ready time from the order (if current order) or else from the server loader
  const requestedReadyTime =
    !clearOrder && orderInfo
      ? orderInfo.requestedReadyTime
      : cachedLoaderData.requestedReadyTime;

  return {
    ...cachedLoaderData,
    orderInfo,
    clearOrder,
    requestedReadyTime: requestedReadyTime,
    tableNumber: cachedLoaderData?.orderInfo?.tableNumber ?? null,
    asapWait,
  };
}
clientLoader.hydrate = true;

export const shouldRevalidate = ({
  actionResult,
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
  if (actionResult?.preventRevalidation) {
    return false;
  }
  // Force revalidation on navigation away from the payments page
  // This allows us to redirect back to the payments page if there is a payment in progress, or delete the payment otherwise
  if (isPaymentPage(currentUrl.pathname) && !isPaymentPage(nextUrl.pathname)) {
    return true;
  }
  return defaultShouldRevalidate;
};

export default function Page() {
  const pageRef = useRef<HTMLDivElement>(null);
  const loaderData = useLoaderData<typeof loader>();

  const location = useLocation();

  // Cancel payments when needed
  // Can't be done in loader as that doesn't handle navigation to parent routes,
  //   and is annoying to do in the clientLoader so would only work on page load
  const fetcher = useFetcher();
  useEffect(() => {
    if (
      fetcher.state === "idle" &&
      !isPaymentPage(location.pathname) &&
      !loaderData.clearOrder &&
      loaderData.orderInfo?.pendingPaymentIds.length
    ) {
      console.log("Cancelling payments");
      const formData = new FormData();
      formData.set("isCancelPayment", "true");
      formData.set("orgId", loaderData.siteInfo.orgId);
      formData.set(
        "paymentIds",
        JSON.stringify(loaderData.orderInfo?.pendingPaymentIds),
      );
      fetcher.submit(formData, {
        method: "post",
      });
    }
  }, [
    location.pathname,
    fetcher,
    loaderData.orderInfo?.pendingPaymentIds,
    loaderData.clearOrder,
    loaderData.siteInfo.orgId,
  ]);

  const { template } = useContext(ThemeContext);
  const { authenticatedUser } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);

  // Task 13: show a toast summarising items dropped during a changeSite
  const droppedItemsRef = useRef<typeof loaderData.droppedItems | null>(null);
  useEffect(() => {
    const items = loaderData.droppedItems;
    if (!items?.length) return;
    // Avoid re-firing on re-renders with the same list
    if (droppedItemsRef.current === items) return;
    (droppedItemsRef as React.MutableRefObject<typeof items | null>).current =
      items;
    const msg = buildDroppedItemsToast(items);
    if (msg) showToast(msg, 8000);
  }, [loaderData.droppedItems, showToast]);

  const savedCustomer = authenticatedUser || loaderData.guestUser;

  const params = useParams();
  invariant(params.orderSource, "expected params.orderSource");
  const orderSource = urlOrderSource[params.orderSource];

  // Styles
  const backgroundColor = template.menuScreen.general.backgroundColour;

  const { menuOpen, serviceAvailable } = getServiceStatus(
    loaderData.siteInfo.site,
    loaderData.menu,
    orderSource,
    loaderData.requestedReadyTime,
  );

  const isOffline =
    !!loaderData.siteInfo.site &&
    (!menuOpen ||
      !serviceAvailable ||
      loaderData.availableTimeSlots?.length === 0);

  const signBackInRequired =
    !!loaderData?.orderInfo &&
    !loaderData?.clearOrder &&
    loaderData?.orderInfo?.customerId != null &&
    loaderData?.orderInfo?.customerId !== authenticatedUser?.id;

  return (
    <SiteContextProvider siteData={loaderData.siteInfo}>
      <TaxContextProvider
        taxes={loaderData.taxes}
        currency={loaderData.currency}
      >
        <MenuContextProvider menus={loaderData.menus} menu={loaderData.menu}>
          <FlashPromotionContextProvider
            flashPromotion={loaderData.flashPromotion}
          >
            <BannerContextProvider banners={loaderData.banners}>
              <OrderContextProvider
                savedCustomer={savedCustomer}
                asapAvailable={loaderData.asapAvailable}
                orderInfo={loaderData.orderInfo}
                clearOrder={loaderData.clearOrder}
                orderSource={orderSource}
                orderTypeCode={loaderData.orderTypeCode}
                deliveryInfo={loaderData.deliveryInfo}
                paramOrderSource={params.orderSource}
                paramMenuCode={params.menuCode}
                availableTimeSlots={loaderData.availableTimeSlots}
                requestedReadyTime={loaderData.requestedReadyTime}
                asapWait={loaderData.asapWait}
                tableNumber={loaderData.tableNumber}
                timeValid={!isOffline}
                signBackInRequired={signBackInRequired}
                sessionRequestedTime={loaderData.sessionRequestedTime}
              >
                <PaymentContextProvider>
                  <BaseLayout
                    orgId={loaderData.siteInfo.orgId}
                    menus={loaderData.menus}
                    menu={loaderData.menu}
                    site={loaderData.siteInfo.site}
                    showOffline={isOffline}
                  >
                    <PageWrapper
                      pageRef={pageRef}
                      loading={false}
                      style={{ backgroundColor, padding: 0 }}
                    >
                      <StoreHeader />
                      <SignBackInModal
                        visible={signBackInRequired}
                        wrongAccount={
                          signBackInRequired && !!authenticatedUser?.id
                        }
                      />
                      <Outlet />
                    </PageWrapper>
                  </BaseLayout>
                </PaymentContextProvider>
              </OrderContextProvider>
            </BannerContextProvider>
          </FlashPromotionContextProvider>
        </MenuContextProvider>
      </TaxContextProvider>
    </SiteContextProvider>
  );
}

export const ErrorBoundary = DefaultErrorBoundary;
